// Midnight expansion survey service
import { app } from '../firebase';
import { 
  getFirestore, 
  collection, 
  doc, 
  addDoc, 
  getDocs, 
  updateDoc, 
  query,
  where,
  orderBy,
  serverTimestamp
} from 'firebase/firestore';

const db = getFirestore(app);

// Role definitions with their available classes
export const ROLE_CLASSES = {
  'Tank': ['Death Knight', 'Demon Hunter', 'Druid', 'Monk', 'Paladin', 'Warrior'],
  'Healer': ['Druid', 'Evoker', 'Monk', 'Paladin', 'Priest', 'Shaman'],
  'Melee DPS': ['Death Knight', 'Demon Hunter', 'Druid', 'Hunter', 'Monk', 'Paladin', 'Rogue', 'Shaman', 'Warrior'],
  'Ranged DPS': ['Demon Hunter', 'Druid', 'Evoker', 'Hunter', 'Mage', 'Priest', 'Shaman', 'Warlock'],
};

export type GeneralThoughtsOption = 
  | 'I know what I want to play'
  | 'I have some ideas on what I want to play'
  | 'I am open to filling raid team needs'
  | "I don't know what I want to play";

export type RoleType = 'Tank' | 'Healer' | 'Melee DPS' | 'Ranged DPS';

export interface RoleInterest {
  absolutelyNot: boolean;
  interestLevel: number; // 1-5
  classes: string[]; // Selected classes for this role
}

export interface MidnightSurveyResponse {
  id: string;
  userId: string;
  username: string;
  generalThoughts: GeneralThoughtsOption;
  roles: {
    [key in RoleType]: RoleInterest;
  };
  submittedAt: any; // Firestore timestamp
}

/**
 * Submit or update a Midnight survey response
 */
export async function submitMidnightSurvey(
  userId: string,
  username: string,
  generalThoughts: GeneralThoughtsOption,
  roles: { [key in RoleType]: RoleInterest }
): Promise<string | null> {
  try {
    // Validate interest levels
    for (const roleType of Object.keys(roles) as RoleType[]) {
      const role = roles[roleType];
      if (!role.absolutelyNot && (role.interestLevel < 1 || role.interestLevel > 5)) {
        return `Interest level for ${roleType} must be between 1 and 5`;
      }
      // Validate classes are valid for the role
      const validClasses = ROLE_CLASSES[roleType];
      for (const className of role.classes) {
        if (!validClasses.includes(className)) {
          return `${className} is not a valid class for ${roleType}`;
        }
      }
    }

    // Check if user already has a submission
    const existingQuery = query(
      collection(db, 'midnight_surveys'),
      where('userId', '==', userId)
    );
    const existingDocs = await getDocs(existingQuery);

    const surveyData: any = {
      username,
      generalThoughts,
      roles,
      submittedAt: serverTimestamp(),
    };

    if (!existingDocs.empty) {
      // Update existing submission
      const docId = existingDocs.docs[0].id;
      await updateDoc(doc(db, 'midnight_surveys', docId), surveyData);
    } else {
      // Create new submission
      surveyData.userId = userId;
      await addDoc(collection(db, 'midnight_surveys'), surveyData);
    }

    return null; // Success
  } catch (error) {
    console.error('Error submitting Midnight survey:', error);
    return 'Failed to submit survey';
  }
}

/**
 * Get a user's Midnight survey response
 */
export async function getUserMidnightSurvey(userId: string): Promise<MidnightSurveyResponse | null> {
  try {
    const q = query(
      collection(db, 'midnight_surveys'),
      where('userId', '==', userId)
    );
    const snapshot = await getDocs(q);
    
    if (snapshot.empty) {
      return null;
    }
    
    const doc = snapshot.docs[0];
    return {
      id: doc.id,
      ...doc.data()
    } as MidnightSurveyResponse;
  } catch (error) {
    console.error('Error fetching user Midnight survey:', error);
    return null;
  }
}

/**
 * Get all Midnight survey responses (admin only)
 */
export async function getAllMidnightSurveys(): Promise<MidnightSurveyResponse[]> {
  try {
    const q = query(
      collection(db, 'midnight_surveys'),
      orderBy('submittedAt', 'desc')
    );
    const snapshot = await getDocs(q);
    
    const surveys: MidnightSurveyResponse[] = [];
    snapshot.forEach((doc) => {
      surveys.push({
        id: doc.id,
        ...doc.data()
      } as MidnightSurveyResponse);
    });
    
    return surveys;
  } catch (error) {
    console.error('Error fetching all Midnight surveys:', error);
    return [];
  }
}

// Map general thoughts response to shorter version for table/CSV display
const mapGeneralThoughts = (thoughts: string): string => {
  const mapping: { [key: string]: string } = {
    'I know what I want to play': 'Locked in',
    'I have some ideas on what I want to play': 'Debating some options',
    'I am open to filling raid team needs': 'Filler',
    "I don't know what I want to play": 'No idea',
  };
  return mapping[thoughts] || thoughts;
};

// Format role name for display (shorten Melee DPS and Ranged DPS)
const formatRoleName = (roleType: RoleType): string => {
  if (roleType === 'Melee DPS') return 'Melee';
  if (roleType === 'Ranged DPS') return 'Ranged';
  return roleType;
};

// Get all selected roles for a survey (all roles not marked as absolutely not)
const getAllSelectedRoles = (survey: MidnightSurveyResponse): RoleType[] => {
  const selectedRoles: RoleType[] = [];
  
  for (const roleType of Object.keys(survey.roles) as RoleType[]) {
    const role = survey.roles[roleType];
    if (!role.absolutelyNot) {
      selectedRoles.push(roleType);
    }
  }
  
  return selectedRoles;
};

/**
 * Export survey data to CSV format
 * One row per player per role, showing classes for that specific role
 */
export function exportSurveysToCSV(surveys: MidnightSurveyResponse[]): string {
  // Get all unique classes across all roles
  const allClasses = new Set<string>();
  Object.values(ROLE_CLASSES).forEach(classes => {
    classes.forEach(className => allClasses.add(className));
  });
  const sortedClasses = Array.from(allClasses).sort();

  // Build CSV header
  const headers = [
    'User/Player',
    'Mindset',
    'Role',
    'Rating',
    ...sortedClasses
  ];

  // Expand surveys into rows - one row per player per role
  const expandedRows: Array<{
    username: string;
    generalThoughts: string;
    roleType: RoleType;
    roleRating: number;
    classes: string[];
  }> = [];

  surveys.forEach(survey => {
    const selectedRoles = getAllSelectedRoles(survey);
    
    selectedRoles.forEach(roleType => {
      const role = survey.roles[roleType];
      expandedRows.push({
        username: survey.username,
        generalThoughts: mapGeneralThoughts(survey.generalThoughts),
        roleType,
        roleRating: role.interestLevel,
        classes: role.classes,
      });
    });
  });

  // Build CSV rows
  const rows = expandedRows.map(row => {
    // Build class columns - mark which classes are selected for this specific role
    const classData = sortedClasses.map(className => {
      return row.classes.includes(className) ? 'X' : '';
    });

    return [
      row.username,
      row.generalThoughts,
      formatRoleName(row.roleType),
      String(row.roleRating),
      ...classData
    ];
  });

  // Combine headers and rows
  const csvRows = [headers, ...rows];

  // Convert to CSV string
  return csvRows.map(row => 
    row.map(cell => {
      // Escape quotes and wrap in quotes if contains comma or newline
      const cellStr = String(cell || '');
      if (cellStr.includes(',') || cellStr.includes('"') || cellStr.includes('\n')) {
        return `"${cellStr.replace(/"/g, '""')}"`;
      }
      return cellStr;
    }).join(',')
  ).join('\n');
}

